import React, { useContext, useReducer } from 'react';
import { AxiosError } from 'axios';
import { ErrorMessage } from 'types/shared/error-message.type';

enum SnackbarActionTypes {
  ERROR_SNACKBAR = 'ERROR_SNACKBAR',
  SUCCESS_SNACKBAR = 'SUCCESS_SNACKBAR',
  CLEAR_SNACKBAR = 'CLEAR_SNACKBAR',
  WARNING_SNACKBAR = 'WARNING_SNACKBAR',
}

type SnackbarButtonAction = {
  label: string;
  onClick: () => void;
};

type Action =
  | { type: SnackbarActionTypes.ERROR_SNACKBAR; payload: { msg: string; error: AxiosError; errorMsg?: ErrorMessage[] } }
  | { type: SnackbarActionTypes.SUCCESS_SNACKBAR; payload: { msg: string; action?: SnackbarButtonAction } }
  | { type: SnackbarActionTypes.CLEAR_SNACKBAR }
  | { type: SnackbarActionTypes.WARNING_SNACKBAR; payload: { msg: string } };

type Dispatch = (action: Action) => void;

type State = {
  isError: boolean;
  isSuccess: boolean;
  isWarning: boolean;
  msg: string;
  error: AxiosError | undefined;
  errorMsg?: ErrorMessage[];
  action?: SnackbarButtonAction;
};

const SnackbarStateContext = React.createContext<State | undefined>(undefined);
const SnackbarDispatchContext = React.createContext<Dispatch | undefined>(undefined);

const initialState: State = {
  isError: false,
  isSuccess: false,
  isWarning: false,
  msg: '',
  error: undefined,
  errorMsg: undefined,
  action: undefined,
};

function SnackbarReducer(state: State, action: Action): State {
  switch (action.type) {
    case SnackbarActionTypes.ERROR_SNACKBAR:
      return {
        ...state,
        isError: true,
        msg: action.payload.msg,
        error: action.payload.error,
        errorMsg: action.payload.errorMsg,
        action: undefined,
      };

    case SnackbarActionTypes.SUCCESS_SNACKBAR:
      return {
        ...state,
        isSuccess: true,
        msg: action.payload.msg,
        action: action.payload.action,
      };

    case SnackbarActionTypes.WARNING_SNACKBAR:
      return {
        ...state,
        isWarning: true,
        msg: action.payload.msg,
        action: undefined,
      };

    case SnackbarActionTypes.CLEAR_SNACKBAR:
      return initialState;

    default:
      return state;
  }
}

type SnackbarProviderProps = {
  children: React.ReactNode;
};

const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(SnackbarReducer, initialState);

  return (
    <SnackbarStateContext.Provider value={state}>
      <SnackbarDispatchContext.Provider value={dispatch}>{children}</SnackbarDispatchContext.Provider>
    </SnackbarStateContext.Provider>
  );
};

function useSnackbarState() {
  const context = useContext(SnackbarStateContext);
  if (context === undefined) {
    throw new Error('useSnackbarState must be used within a SnackbarProvider');
  }
  return context;
}

function useSnackbarDispatch() {
  const context = useContext(SnackbarDispatchContext);
  if (context === undefined) {
    throw new Error('useSnackbarDispatch must be used within a SnackbarProvider');
  }
  return context;
}

export { SnackbarProvider, useSnackbarState, useSnackbarDispatch, SnackbarActionTypes };
